/**
 * Signup Card Component
 *
 * The one sign-up form of the app (30.10.2026: the separate /landing/signup page is gone). It
 * appears in the side panel of the entry page, inside the chat when the agent offers the trial,
 * and in the phone overlay. Google first; the e-mail form opens on demand and asks only what an
 * account needs: name, e-mail, password. The company is asked later in the setup wizard.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { usePublicAuth } from '../hooks/use-public-auth';
import { trackEntry, useEntry } from '../entry-context';
import GoogleSignInButton from 'features/account/google-sign-in/google-sign-in-button';
import { startGoogleSignIn } from 'features/account/google-sign-in/google-sign-in';
import i18n from 'i18n';

/** Same legal documents the website links to. */
const AGB_URL = '/dashboard/legal/agb.html';
const PRIVACY_URL = 'https://secret-ai-agent.de/datenschutz';
const DPA_URL = '/dashboard/legal/avv.html';

/** The API accepts 6–50 characters; the form asks for 8 so accounts start with a sturdier password. */
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 50;

interface SignupCardProps {
  /** Shown above the form (the chat card); the side panel has its own heading. */
  title?: string;
  /** Start with the e-mail form open (phone overlay). */
  initiallyExpanded?: boolean;
  /** Report `register_view` when the card appears (chat card, overlay). */
  trackView?: boolean;
  /** Where the card sits, for the measurement: rail, chat, overlay. */
  place: string;
  onSwitchToLogin?: () => void;
  onClose?: () => void;
}

type FormField = 'firstName' | 'lastName' | 'email' | 'password';

export const SignupCard: React.FC<SignupCardProps> = ({ title, initiallyExpanded = false, trackView = false, place, onSwitchToLogin, onClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const entryCtx = useEntry();
  const { signup, isLoading, error, clearError } = usePublicAuth();

  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [formData, setFormData] = useState<Record<FormField, string>>({ firstName: '', lastName: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Optional consents (Engagement Center). Never pre-ticked — § 7 UWG needs an active opt-in.
  const [consentMarketingEmail, setConsentMarketingEmail] = useState(false);
  const [consentAdMeasurement, setConsentAdMeasurement] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [googleNeedsTerms, setGoogleNeedsTerms] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (trackView) trackEntry(entryCtx, 'register_view', undefined, { cta: place });
    // Once per appearance of the card.
  }, []);

  const markStarted = () => {
    if (started.current) return;
    started.current = true;
    trackEntry(entryCtx, 'register_start', undefined, { cta: place });
  };

  const openEmailForm = () => {
    setExpanded(true);
    if (!trackView) trackEntry(entryCtx, 'register_view', undefined, { cta: place });
  };

  const handleChange = (field: FormField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    markStarted();
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    clearError();
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!formData.firstName.trim()) errors.firstName = t('publicChat.errorFirstName', 'Bitte gib deinen Vornamen ein.');
    if (!formData.lastName.trim()) errors.lastName = t('publicChat.errorLastName', 'Bitte gib deinen Nachnamen ein.');
    if (!formData.email.trim()) {
      errors.email = t('publicChat.errorEmailRequired', 'Bitte gib deine E-Mail-Adresse ein.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = t('publicChat.errorEmailInvalid', 'Bitte gib eine gültige E-Mail-Adresse ein.');
    }
    if (!formData.password) {
      errors.password = t('publicChat.errorPasswordRequired', 'Bitte wähle ein Passwort.');
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      errors.password = t('publicChat.errorPasswordShort', 'Das Passwort braucht mindestens {{count}} Zeichen.', { count: PASSWORD_MIN_LENGTH });
    } else if (formData.password.length > PASSWORD_MAX_LENGTH) {
      errors.password = t('publicChat.errorPasswordLong', 'Das Passwort darf höchstens {{count}} Zeichen haben.', { count: PASSWORD_MAX_LENGTH });
    }
    if (!termsAccepted) errors.terms = t('publicChat.errorTerms', 'Bitte stimme den AGB und der Datenschutzerklärung zu.');
    setValidationErrors(errors);
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackEntry(entryCtx, 'register_submit', undefined, { cta: place });
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      // Only the place, never the value: password, or everything else counted as the e-mail block.
      trackEntry(entryCtx, 'register_error', errors.password ? 'password' : 'email', { cta: place });
      return;
    }
    await signup(
      {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        acceptedLegalTerms: true,
        attribution: entryCtx.attribution,
        consentMarketingEmail,
        consentAdMeasurement,
      },
      entryCtx.sessionId || ''
    );
  };

  const handleGoogle = (clientId: string) => {
    if (!termsAccepted) {
      setGoogleNeedsTerms(true);
      return;
    }
    trackEntry(entryCtx, 'register_submit', 'google', { cta: place });
    const language = (i18n.language || 'de').split('-')[0];
    startGoogleSignIn(clientId, {
      signup: {
        acceptedLegalTerms: true,
        language: ['en', 'de'].includes(language) ? language : 'de',
        attribution: entryCtx.attribution,
        consentMarketingEmail,
        consentAdMeasurement,
      },
      chatSessionId: entryCtx.sessionId || undefined,
      entry: entryCtx.entry,
    });
  };

  const visibilityToggle = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPassword((prev) => !prev)}
        edge="end"
        size="small"
        aria-label={showPassword ? t('publicChat.hidePassword', 'Passwort verbergen') : t('publicChat.showPassword', 'Passwort anzeigen')}
      >
        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box className={classes.root}>
      {(title || onClose) && (
        <Box className={classes.header}>
          {title && <Typography className={classes.title}>{title}</Typography>}
          {onClose && (
            <IconButton onClick={onClose} size="small" aria-label={t('entry.close')} className={classes.closeButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {entryCtx.googleNoAccount && <Typography className={classes.note}>{t('googleSignInNoAccount')}</Typography>}
      {error && (
        <Alert severity="error" className={classes.alert}>
          {error}
        </Alert>
      )}

      <GoogleSignInButton text={t('googleSignUp')} onClick={handleGoogle} />

      {!expanded ? (
        <Button variant="outlined" fullWidth onClick={openEmailForm} className={classes.emailButton}>
          {t('entry.emailSignup')}
        </Button>
      ) : (
        <Typography className={classes.divider}>{t('entry.orEmail')}</Typography>
      )}

      <form onSubmit={handleSubmit} className={classes.form} noValidate>
        {expanded && (
          <>
            <Box className={classes.nameRow}>
              <TextField
                label={t('publicChat.firstName', 'Vorname')}
                value={formData.firstName}
                onChange={handleChange('firstName')}
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
                size="small"
                fullWidth
                autoComplete="given-name"
              />
              <TextField
                label={t('publicChat.lastName', 'Nachname')}
                value={formData.lastName}
                onChange={handleChange('lastName')}
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
                size="small"
                fullWidth
                autoComplete="family-name"
              />
            </Box>
            <TextField
              label={t('publicChat.email', 'E-Mail')}
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              size="small"
              fullWidth
              autoComplete="email"
            />
            <TextField
              label={t('publicChat.password', 'Passwort')}
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              error={!!validationErrors.password}
              helperText={validationErrors.password || t('publicChat.passwordHint', 'Mindestens {{count}} Zeichen.', { count: PASSWORD_MIN_LENGTH })}
              size="small"
              fullWidth
              autoComplete="new-password"
              inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
              InputProps={{ endAdornment: visibilityToggle }}
            />
          </>
        )}

        {/* Needed for Google as well: the terms come before any account. */}
        <Box className={cx(classes.consentBox, (validationErrors.terms || googleNeedsTerms) && classes.consentBoxError)}>
          <FormControlLabel
            className={classes.termsControl}
            control={
              <Checkbox
                checked={termsAccepted}
                size="small"
                onChange={(event) => {
                  markStarted();
                  setTermsAccepted(event.target.checked);
                  setGoogleNeedsTerms(false);
                  setValidationErrors((prev) => ({ ...prev, terms: '' }));
                }}
              />
            }
            label={
              <Typography variant="caption" className={classes.termsLabel}>
                {t('acceptTermsAndPrivacy', 'Ich habe die AGB und die Datenschutzerklärung gelesen und stimme ihnen zu.')}
              </Typography>
            }
          />
          <Box className={classes.termsLinks}>
            <Link href={AGB_URL} target="_blank" rel="noopener" variant="caption" underline="hover">
              {t('termsOfUse', 'AGB')}
            </Link>
            <Link href={PRIVACY_URL} target="_blank" rel="noopener" variant="caption" underline="hover">
              {t('privacyPolicy', 'Datenschutzerklärung')}
            </Link>
            <Link href={DPA_URL} target="_blank" rel="noopener" variant="caption" underline="hover">
              {t('dataProcessingAgreement', 'Auftragsverarbeitung')}
            </Link>
          </Box>
          {googleNeedsTerms && <Typography className={classes.note}>{t('googleSignInNeedsTerms')}</Typography>}
        </Box>

        {/* Optional consents: shown once the visitor starts (form open or terms ticked), so the
            first view stays short. TODO(legal): wording pending legal review (same keys as before). */}
        {(expanded || termsAccepted) && (
          <>
            <FormControlLabel
              className={classes.termsControl}
              control={<Checkbox checked={consentMarketingEmail} size="small" onChange={(event) => setConsentMarketingEmail(event.target.checked)} />}
              label={<Typography variant="caption" className={classes.termsLabel}>{t('engSignupConsentMarketing')}</Typography>}
            />
            <FormControlLabel
              className={classes.termsControl}
              control={<Checkbox checked={consentAdMeasurement} size="small" onChange={(event) => setConsentAdMeasurement(event.target.checked)} />}
              label={<Typography variant="caption" className={classes.termsLabel}>{t('engSignupConsentAdMeasurement')}</Typography>}
            />
          </>
        )}

        {expanded && (
          <Button type="submit" variant="contained" fullWidth disabled={isLoading} className={classes.submitButton}>
            {isLoading ? <CircularProgress size={22} color="inherit" /> : t('entry.submitSignup')}
          </Button>
        )}
        <Typography className={classes.paymentNote}>{t('entry.paymentNote')}</Typography>
      </form>

      {onSwitchToLogin && (
        <Button variant="text" size="small" onClick={onSwitchToLogin} className={classes.switchButton}>
          {t('entry.toLogin')}
        </Button>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 800,
    fontSize: '1.05rem',
  },
  closeButton: {
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  alert: {
    borderRadius: 10,
  },
  note: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
  emailButton: {
    height: 48,
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
  },
  divider: {
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
    },
  },
  nameRow: {
    display: 'flex',
    gap: theme.spacing(1.25),
  },
  consentBox: {
    borderRadius: 10,
    padding: theme.spacing(0.75, 1.25, 1),
    backgroundColor: theme.palette.backgrounds?.main_new ?? theme.palette.grey[50],
    border: '1px solid transparent',
  },
  consentBoxError: {
    borderColor: theme.palette.error.main,
  },
  termsControl: {
    alignItems: 'flex-start',
    margin: 0,
  },
  termsLabel: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: 'block',
    paddingTop: theme.spacing(0.75),
  },
  termsLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 1.5),
    paddingLeft: theme.spacing(3.75),
  },
  submitButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(15),
    padding: theme.spacing(1.4),
    borderRadius: 10,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none',
    },
  },
  paymentNote: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
  },
  switchButton: {
    alignSelf: 'center',
    color: theme.palette.primary.main,
    fontWeight: 600,
    textTransform: 'none',
  },
}));

export default SignupCard;
